<template>
  <AuthLayout
    title="Mot de passe oublié"
    subtitle="Entrez votre email pour recevoir un lien de réinitialisation"
    show-back-link
  >
    <!-- Success Message -->
    <div v-if="success" class="space-y-6">
      <div class="p-4 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-success-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
          </svg>
          <div>
            <p class="font-medium text-success-800 dark:text-success-200">Email envoyé !</p>
            <p class="text-sm text-success-700 dark:text-success-300 mt-1">
              Si un compte existe avec cette adresse, vous recevrez un email avec les instructions.
            </p>
          </div>
        </div>
      </div>

      <p class="text-sm text-corporate-600 dark:text-corporate-400">
        Vous n'avez pas reçu l'email ?
        <button
          type="button"
          class="font-medium text-accent-600 hover:text-accent-700"
          @click="success = false"
        >
          Réessayer
        </button>
      </p>
    </div>

    <!-- Form -->
    <form v-else @submit.prevent="handleSubmit" class="space-y-5">
      <!-- Error Alert -->
      <div
        v-if="error"
        class="p-4 rounded-lg bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800"
      >
        <div class="flex items-start gap-3">
          <svg class="w-5 h-5 text-error-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
          </svg>
          <p class="text-sm text-error-700 dark:text-error-400">{{ error }}</p>
        </div>
      </div>

      <!-- Email -->
      <div>
        <label for="email" class="block text-sm font-medium text-corporate-700 dark:text-corporate-300 mb-1.5">
          Adresse email
        </label>
        <input
          v-model="form.email"
          type="email"
          id="email"
          placeholder="vous@exemple.com"
          autocomplete="email"
          class="w-full h-11 px-4 rounded-lg border border-corporate-300 bg-white text-corporate-900 placeholder:text-corporate-400 transition-all duration-150 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/20 focus:outline-none dark:bg-corporate-900 dark:border-corporate-700 dark:text-white"
        />
      </div>

      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="isLoading"
        class="w-full h-11 flex items-center justify-center gap-2 rounded-lg border-2 border-corporate-800 bg-transparent hover:bg-corporate-800 text-corporate-800 hover:text-white dark:border-0 dark:bg-[#1E3A5F] dark:hover:bg-[#152238] dark:text-white font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-corporate-400/50 dark:focus:ring-[#1E3A5F]/50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <svg v-if="isLoading" class="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Envoyer le lien
      </button>
    </form>
  </AuthLayout>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { AuthLayout } from '@/layouts'
import { useAuth } from '@/composables'

const { forgotPassword } = useAuth()

const form = reactive({
  email: ''
})

const error = ref('')
const success = ref(false)
const isLoading = ref(false)

const handleSubmit = async () => {
  error.value = ''
  isLoading.value = true

  if (!form.email) {
    error.value = 'L\'email est requis'
    isLoading.value = false
    return
  }

  try {
    const result = await forgotPassword(form.email)

    if (!result.success) {
      error.value = result.error || 'Erreur lors de l\'envoi'
    } else {
      success.value = true
    }
  } catch (e: any) {
    error.value = e.message || 'Une erreur est survenue'
  } finally {
    isLoading.value = false
  }
}
</script>
